import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import { apiInstance } from '../axiosApi';

/**
 * VoiceBroadcast prepaid billing.
 *
 * Master admin : slab pricing per company, credit top-ups, all-company overview.
 * Tenant       : own Total / Used / Pending credit, own credit + call reports.
 *
 * The tenant endpoints scope themselves from the JWT, so a tenant never sends
 * company_id. Master admin has no companyId claim and must pass ?company_id=.
 */

// ---- master admin ---------------------------------------------------------

export const vbAccountsGet = createAsyncThunk('vb-billing/accounts', async () => {
  return await apiInstance.get('/voicebroadcast-billing/admin/accounts');
});

export const vbPricingGet = createAsyncThunk('vb-billing/pricing-get', async (companyId) => {
  return await apiInstance.get(`/voicebroadcast-billing/admin/pricing/${companyId}`);
});

export const vbPricingSave = createAsyncThunk('vb-billing/pricing-save', async (payload) => {
  return await apiInstance.post('/voicebroadcast-billing/admin/pricing', payload);
});

export const vbAddCredit = createAsyncThunk('vb-billing/add-credit', async (payload) => {
  return await apiInstance.post('/voicebroadcast-billing/admin/add-credit', payload);
});

// ---- tenant (and master admin with company_id) ----------------------------

export const vbBalanceGet = createAsyncThunk('vb-billing/balance', async (params) => {
  return await apiInstance.get('/voicebroadcast-billing/balance', { params });
});

export const vbCreditReportGet = createAsyncThunk('vb-billing/credit-report', async (params) => {
  return await apiInstance.get('/voicebroadcast-billing/credit-report', { params });
});

export const vbCallReportGet = createAsyncThunk('vb-billing/call-report', async (params) => {
  return await apiInstance.get('/voicebroadcast-billing/call-report', { params });
});

const initialState = {
  vbAccounts: null,
  vbBalance: null
};

const voiceBroadcastBillingSlice = createSlice({
  name: 'voiceBroadcastBilling',
  initialState,
  reducers: {
    clearVoiceBroadcastBillingSlice: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(vbAccountsGet.fulfilled, (state, action) => {
      state.vbAccounts = action?.payload;
    });
    builder.addCase(vbBalanceGet.fulfilled, (state, action) => {
      state.vbBalance = action?.payload;
    });
  }
});

export const { clearVoiceBroadcastBillingSlice } = voiceBroadcastBillingSlice.actions;

export const selectVbAccounts = (state) => state.voiceBroadcastBilling.vbAccounts;
export const useVbAccounts = () => {
  const vbAccounts = useSelector(selectVbAccounts);
  return useMemo(() => vbAccounts, [vbAccounts]);
};

export const selectVbBalance = (state) => state.voiceBroadcastBilling.vbBalance;
export const useVbBalance = () => {
  const vbBalance = useSelector(selectVbBalance);
  return useMemo(() => vbBalance, [vbBalance]);
};

export default voiceBroadcastBillingSlice.reducer;
